// The durable write-behind queue. Every local mutation to a synced collection
// records its intent here so a save made while offline (or before a backend is
// attached) is not lost — the controller drains the outbox once a backend is
// connected, retrying on the next flush if a push fails.
//
// Keyed by `${table}:${recordId}`, so a record that is edited repeatedly before
// it flushes collapses to ONE pending entry (the queue tracks intent, not an
// op log) — it can't grow unbounded per record. The entry stores only a
// reference; the controller re-reads the live local record at flush time, so the
// payload is never duplicated or left stale.

import type { IDBStore } from "../model/idb-store";
import { createScopedStore } from "./account-scope";

export type OutboxOpType = "put" | "delete";

// `updatedAt` doubles as the op timestamp (the LWW key carried up for a delete,
// which has no local record to read) and the IDBStore listing sort key.
export interface OutboxEntry {
  id: string; // `${table}:${recordId}`
  table: string;
  recordId: string;
  type: OutboxOpType;
  updatedAt: number;
}

export interface Outbox {
  put(entry: OutboxEntry): Promise<void>;
  all(): Promise<OutboxEntry[]>; // oldest first (flush order)
  remove(id: string): Promise<void>;
}

// `store` is injectable so tests run against an in-memory fake (the suite mocks
// idb-store and never touches IndexedDB); production uses its own database so it
// versions independently of the model/layout libraries. The default is scoped
// per account: queued entries reference records in that account's pool, so an
// unscoped outbox would let one account's pending deletes flush as tombstones
// into the next account that signs in.
export function createOutbox(
  store: IDBStore<OutboxEntry> = createScopedStore<OutboxEntry>("tinydraft-sync", "outbox"),
): Outbox {
  return {
    put: (entry) => store.put(entry).then(() => undefined),
    // IDBStore.list() is newest-first; flush wants oldest-first so earlier
    // edits push before later ones.
    all: () => store.list().then((all) => all.reverse()),
    remove: (id) => store.remove(id).then(() => undefined),
  };
}
